/* `/channels` — the doors people use to reach an agent: a Telegram bot, a
   Slack app, a linked WhatsApp number. `tasks/interfaces/00-PLAN.md` C4 for the
   wire.

   In `shared/api/` with the other clients. The chat views need none of it: a
   thread carries its own `channel_id` and `via`.

   Paths are bare; `client.baseURL` carries `/api/v1`. Every enveloped answer
   goes through `unwrap` — DELETE is the one route with no envelope, a 204. */
import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { threadKeys } from "./threads";

import type {
  Interface,
  InterfaceCreate,
  InterfaceCredentials,
  InterfacePatch,
  InterfacePlace,
  PairingCode,
  SimulateBody,
  SimulateStarted,
  WhatsappLink,
} from "@/shared/types";

export const interfaceKeys = {
  all: ["interfaces"] as const,
  lists: () => [...interfaceKeys.all, "list"] as const,
  list: (agentId: string) => [...interfaceKeys.lists(), agentId] as const,
  link: (id: string) => [...interfaceKeys.all, "link", id] as const,
  places: (id: string) => [...interfaceKeys.all, "places", id] as const,
};

/* ------------------------------ endpoints -------------------------------- */

/** An agent's interfaces, each with its 30-day count and 14-day traffic. */
export async function listInterfaces(agentId: string): Promise<Interface[]> {
  return unwrap(await client.get<Envelope<Interface[]>>("/channels", { params: { agent_id: agentId } }));
}

export async function getInterface(id: string): Promise<Interface> {
  return unwrap(await client.get<Envelope<Interface>>(`/channels/${id}`));
}

/** Telegram connects in this one call; Slack and WhatsApp start as `needs_setup`. */
export async function createInterface(body: InterfaceCreate): Promise<Interface> {
  return unwrap(await client.post<Envelope<Interface>>("/channels", body));
}

export async function patchInterface(id: string, body: InterfacePatch): Promise<Interface> {
  return unwrap(await client.patch<Envelope<Interface>>(`/channels/${id}`, body));
}

/** Slack's second step, or a new token for a bot whose old one stopped working. */
export async function putInterfaceCredentials(id: string, body: InterfaceCredentials): Promise<Interface> {
  return unwrap(await client.put<Envelope<Interface>>(`/channels/${id}/credentials`, body));
}

/** Asks the provider again; the answer carries the refreshed `connection` and `error`. */
export async function checkInterface(id: string): Promise<Interface> {
  return unwrap(await client.post<Envelope<Interface>>(`/channels/${id}/check`));
}

/** A message as if someone had sent it on the channel; the agent answers it on a thread. */
export async function simulateInterface(id: string, body: SimulateBody): Promise<SimulateStarted> {
  return unwrap(await client.post<Envelope<SimulateStarted>>(`/channels/${id}/simulate`, body));
}

/** WhatsApp only: where linking stands. A read never starts anything. */
export async function getWhatsappLink(id: string): Promise<WhatsappLink> {
  return unwrap(await client.get<Envelope<WhatsappLink>>(`/channels/${id}/link`));
}

/** Discord and Teams: the servers or teams the bot is in, with their channels. */
export async function getInterfacePlaces(id: string): Promise<InterfacePlace[]> {
  return unwrap(await client.get<Envelope<InterfacePlace[]>>(`/channels/${id}/places`));
}

/** Teams only: the app zip to upload in Teams. It needs the auth header, so it is fetched through
    the client and saved from a blob; a plain link would go out bare. */
export async function downloadTeamsApp(id: string): Promise<void> {
  const res = await client.get<Blob>(`/channels/${id}/teams-app`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "definable-teams-app.zip";
  a.click();
  URL.revokeObjectURL(url);
}

/** WhatsApp only: a fresh linking session with a new code — "Show a new code". */
export async function startWhatsappLink(id: string): Promise<WhatsappLink> {
  return unwrap(await client.post<Envelope<WhatsappLink>>(`/channels/${id}/link`));
}

/* Before it can pair, the bridge waits up to 20 s for WhatsApp's first code, and
   the backend gives it at least 30 s (task 06) — past the client's 15 s. Waiting
   longer than the backend lets a slow WhatsApp end in the backend's own answer
   instead of a timeout here. */
const PAIRING_TIMEOUT_MS = 45_000;

/** WhatsApp only: a code to type on the phone instead of scanning. */
export async function requestPairingCode(id: string, phone: string): Promise<PairingCode> {
  return unwrap(
    await client.post<Envelope<PairingCode>>(`/channels/${id}/link/code`, { phone }, { timeout: PAIRING_TIMEOUT_MS }),
  );
}

/** Disconnects at the provider, then removes the interface. */
export async function deleteInterface(id: string): Promise<void> {
  await client.delete(`/channels/${id}`);
}

/* -------------------------------- hooks ---------------------------------- */

const LIST_REFRESH_MS = 30_000;

/* The list changes on the server's side — messages arrive, a phone logs a number out, a token is
   revoked — so an open page re-reads it every 30 s instead of going stale. */
export function useInterfaces(agentId: string) {
  return useQuery({
    queryKey: interfaceKeys.list(agentId),
    queryFn: () => listInterfaces(agentId),
    enabled: !!agentId,
    refetchInterval: LIST_REFRESH_MS,
  });
}

const LINK_POLL_MS = 2_000;

/* Asked every 2 s while the code is on screen, and never after: a linked number
   or an ended session stops the poll, and closing the dialog drops the query.
   `gcTime: 0` so reopening never shows an old code, and no retries because the
   next poll is the retry. Once linking settles either way the row has changed
   — it gained a number, or an error saying why not — so the lists are re-read. */
export function useWhatsappLink(id: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: interfaceKeys.link(id ?? ""),
    queryFn: async () => {
      const link = await getWhatsappLink(id as string);
      if (link.state !== "waiting") qc.invalidateQueries({ queryKey: interfaceKeys.lists() });
      return link;
    },
    enabled: !!id,
    refetchInterval: (query) => (query.state.data?.state === "waiting" ? LINK_POLL_MS : false),
    gcTime: 0,
    retry: false,
  });
}

/* The picker's list. The service keeps it 60 s itself, so the dialog asks once
   per opening and a failure shows in the dialog, not a toast. */
export function useInterfacePlaces(id: string | null) {
  return useQuery({
    queryKey: interfaceKeys.places(id ?? ""),
    queryFn: () => getInterfacePlaces(id as string),
    enabled: !!id,
    gcTime: 0,
    retry: false,
    meta: { silent: true },
  });
}

/* Writes refresh the lists. The link query keeps itself current: it is polled
   while a code is on screen, and a new code's answer is written straight into it. */
function useInterfacesMutation<V, T>(mutationFn: (vars: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn, onSuccess: () => qc.invalidateQueries({ queryKey: interfaceKeys.lists() }) });
}

export const usePatchInterface = () =>
  useInterfacesMutation((v: { id: string; body: InterfacePatch }) => patchInterface(v.id, v.body));

export const useCheckInterface = () => useInterfacesMutation(checkInterface);

/** A test message opens the sender's test chat, or carries it on, so the threads are re-read too. */
export function useSimulateInterface() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: SimulateBody }) => simulateInterface(v.id, v.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: interfaceKeys.lists() });
      qc.invalidateQueries({ queryKey: threadKeys.all });
    },
  });
}

/** The dialog shows what went wrong beside the button, so no toast. */
export const useDownloadTeamsApp = () => useMutation({ mutationFn: downloadTeamsApp, meta: { silent: true } });

export const useDeleteInterface = () => useInterfacesMutation(deleteInterface);

/* "Show a new code". Its answer goes straight into the link query, so polling
   picks up from the new session at once. The dialog shows why it failed, so no
   toast. */
export function useStartWhatsappLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: startWhatsappLink,
    onSuccess: (link, id) => {
      qc.setQueryData(interfaceKeys.link(id), link);
      qc.invalidateQueries({ queryKey: interfaceKeys.lists() });
    },
    meta: { silent: true },
  });
}

/** The dialog shows what went wrong next to the phone field, so no toast. */
export function useRequestPairingCode() {
  return useMutation({
    mutationFn: (v: { id: string; phone: string }) => requestPairingCode(v.id, v.phone),
    meta: { silent: true },
  });
}

/* ------------------------ writes that carry secrets ----------------------- */

const blank = (credentials?: InterfaceCredentials) => {
  if (!credentials) return;
  for (const key of Object.keys(credentials)) (credentials as unknown as Record<string, string>)[key] = "";
};

/**
 * Send a body that may hold credentials, without letting them outlive the request.
 *
 * The providers rule (`tasks/byok-providers/07`): a token lives in the request
 * body and nowhere else. React Query keeps a mutation's variables in its cache,
 * so the body comes in through a ref instead, and its credentials are blanked
 * in `finally` whatever the provider said. The setup dialogs show the error
 * next to the field, so the mutation is silent.
 */
function useCredentialsWrite<B extends { credentials?: InterfaceCredentials }>(write: (body: B) => Promise<Interface>) {
  const qc = useQueryClient();
  const next = useRef<B | null>(null);
  const mutation = useMutation({
    mutationFn: async () => {
      const body = next.current;
      next.current = null;
      if (!body) throw new Error("Nothing to send.");
      try {
        return await write(body);
      } finally {
        blank(body.credentials);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: interfaceKeys.lists() }),
    gcTime: 0,
    meta: { silent: true },
  });
  const send = (body: B) => {
    next.current = body;
    return mutation.mutateAsync();
  };
  return { ...mutation, send };
}

/** Every new interface; a Telegram one carries its bot token. */
export const useCreateInterface = () => useCredentialsWrite(createInterface);

/** Slack's second step, and a new token for a bot that stopped working. */
export const useSaveCredentials = () =>
  useCredentialsWrite((body: { id: string; credentials: InterfaceCredentials }) =>
    putInterfaceCredentials(body.id, body.credentials),
  );
